import { type FC, type Ref, useState, useRef, useMemo, forwardRef } from 'react';
import { type CountryCode, type CountryCallingCode } from 'libphonenumber-js';

import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { FormHelper } from '@hh.ru/magritte-ui-form-helper';
import { Input } from '@hh.ru/magritte-ui-input';
import { RegionSelect } from '@hh.ru/magritte-ui-phone-input/RegionSelect';
import { phoneFormatterFactory } from '@hh.ru/magritte-ui-phone-input/phoneFormatter';
import { useCountrySelectDataProvider } from '@hh.ru/magritte-ui-phone-input/useCountrySelectDataProvider';
import { type MetadataVariant, useLibPhoneNumber } from '@hh.ru/magritte-ui-phone-input/useLibPhoneNumber';

import styles from './phoneInput.less';

export interface PhoneInputProps {
    /** Код страны который будет выбран при инициализации компонента */
    defaultCountry?: CountryCode;
    /** Национальная часть номера который будет введен в компонент при инициализации */
    defaultNationalNumber?: string;
    /** Обработчик изменения номера телефона */
    onChange: (data: {
        /** Двухбуквенный код страны */
        country: CountryCode;
        /** Международный телефонный код страны */
        callingCode: CountryCallingCode;
        /** Национальная часть номера, содержит только цифры */
        nationalNumber: string;
        /** Номер телефона в международном формате */
        phone: string;
        /** Флаг валидности номера */
        isValid: boolean;
        /** Флаг загрузки API для валидации номера */
        isPhoneValidationApiLoaded: boolean;
    }) => void;
    /** Обработчик ошибки загрузки API для валидации номера */
    onPhoneValidationApiLoadError?: (error: Error, reload: VoidFunction) => void;
    /** Список стран доступных для выбора */
    availableCountries?: CountryCode[];
    /** Флаг приоритетных стран, которые будут отображаться в начале списка */
    priorityCountries?: CountryCode[];
    /** Флаг валидности номера */
    invalid?: boolean;
    /** Описание которое будет отображаться под полями ввода */
    description?: string;
    /** Сообщение об ошибке валидации, будет отображаться только если установлен флаг invalid */
    errorMessage?: string;
    /** Словарь содержащий названия стран */
    countryNames: Partial<Record<CountryCode, string>>;
    /** Название блока с приоритетными странами */
    priorityCountriesTitle?: string;
    /** Обработчик фокуса на компоненте */
    onFocus?: VoidFunction;
    /** Обработчик потери фокуса на компоненте */
    onBlur?: VoidFunction;
    /**
     * Включает режим парсинга только мобильных номеров, в этом режиме уменьшается время загрузки компонента,
     * но номера фиксированных линий связи не пройдут валидацию
     * */
    mobileOnly?: boolean;
    /** Атрибут data-qa */
    'data-qa'?: string;
    /** Ссылка на обертку полей ввода */
    inputsWrapperRef?: Ref<HTMLDivElement>;
    /** Ссылка на элемент input */
    phoneInputElementRef?: Ref<HTMLInputElement>;
    codeInputElementRef?: Ref<HTMLInputElement>;
    disabled?: boolean;
}

const DEFAULT_PRIORITY_COUNTRIES: CountryCode[] = ['RU', 'BY', 'KZ', 'UZ'] as const;
const notDigitsRegExp = /[\D]/g;

export const PhoneInput: FC<PhoneInputProps> = forwardRef<HTMLDivElement, PhoneInputProps>(
    (
        {
            defaultCountry = 'RU',
            defaultNationalNumber = '',
            onChange,
            availableCountries,
            invalid,
            description,
            errorMessage,
            priorityCountries = DEFAULT_PRIORITY_COUNTRIES,
            onFocus,
            onBlur,
            countryNames,
            priorityCountriesTitle,
            mobileOnly,
            'data-qa': dataQa = 'magritte-phone-input-wrapper',
            inputsWrapperRef,
            phoneInputElementRef,
            codeInputElementRef,
            onPhoneValidationApiLoadError,
            disabled,
        },
        ref
    ) => {
        const metadataVariant: MetadataVariant = useMemo<MetadataVariant>(() => {
            const minimalMetadata =
                availableCountries?.length === DEFAULT_PRIORITY_COUNTRIES.length &&
                DEFAULT_PRIORITY_COUNTRIES.every((country) => availableCountries.includes(country));
            if (minimalMetadata) {
                return 'ru-by-kz-uz';
            }

            const keys = Object.keys(countryNames);
            if (
                keys.length === DEFAULT_PRIORITY_COUNTRIES.length &&
                DEFAULT_PRIORITY_COUNTRIES.every((country) => keys.includes(country))
            ) {
                return 'ru-by-kz-uz';
            }

            return 'max';
        }, [availableCountries, countryNames]);

        const libPhoneNumberAPI = useLibPhoneNumber(metadataVariant, !!mobileOnly, onPhoneValidationApiLoadError);
        const { getCountryCallingCode, isValidPhoneNumber, isLoaded } = libPhoneNumberAPI;
        const [country, setCountry] = useState<CountryCode>(defaultCountry);
        const countryRef = useRef<CountryCode>(country);
        const countryCallingCodeRef = useRef<CountryCallingCode>(getCountryCallingCode(country));
        const focusInsideRef = useRef(false);
        const wrapperRef = useRef<HTMLDivElement>(null);
        const wrapperRefMultiplexer = useMultipleRefs(wrapperRef, inputsWrapperRef);
        const [phoneValue, setPhoneValue] = useState(defaultNationalNumber);
        const phoneInputRef = useRef<HTMLInputElement>(null);
        const inputRefMultiplexer = useMultipleRefs(phoneInputRef, phoneInputElementRef);
        const forceDisabled = useDisabled();
        const isComponentDisabled = forceDisabled || !!disabled;
        countryRef.current = country;
        countryCallingCodeRef.current = getCountryCallingCode(country);

        const { dataProvider: selectDataProvider, options: selectOptions } = useCountrySelectDataProvider({
            availableCountries,
            countryNames,
            priorityCountriesTitle,
            priorityCountries,
            libPhoneNumberAPI,
        });

        const phoneFormatter = useMemo(() => {
            return phoneFormatterFactory({
                // обернуто в промис т.к. форматтер вызывается в процессе рендера BaseInput и не может синхронно
                // вызывать изменения стейта другого компонента
                setCountry: (country) => Promise.resolve().then(() => setCountry(country)),
                currentCountryRef: countryRef,
                libPhoneNumberAPI,
            });
        }, [libPhoneNumberAPI]);

        const onChangeHandler = (country: CountryCode, phoneValue: string) => {
            const callingCode = getCountryCallingCode(country);
            const nationalNumber = phoneValue.replace(notDigitsRegExp, '');
            onChange({
                country,
                callingCode,
                phone: `+${callingCode}${nationalNumber}`,
                nationalNumber,
                isValid: isValidPhoneNumber(nationalNumber, country),
                isPhoneValidationApiLoaded: isLoaded,
            });
        };

        const focusHandler = () => {
            if (focusInsideRef.current) {
                return;
            }

            focusInsideRef.current = true;
            onFocus?.();
        };

        const blurHandler = () => {
            requestAnimationFrame(() => {
                if (wrapperRef.current?.contains(document.activeElement)) {
                    return;
                }

                focusInsideRef.current = false;
                onBlur?.();
            });
        };

        return (
            <div ref={ref}>
                <div
                    className={styles.phoneInputContainer}
                    onFocus={focusHandler}
                    onBlur={blurHandler}
                    ref={wrapperRefMultiplexer}
                    data-qa={dataQa}
                >
                    <RegionSelect
                        ref={codeInputElementRef}
                        country={country}
                        selectDataProvider={selectDataProvider}
                        selectOptions={selectOptions}
                        searchable={selectOptions.length > 10}
                        callingCode={countryCallingCodeRef.current}
                        onChange={(newCountry) => {
                            let currentPhoneValue = phoneValue;
                            if (country !== newCountry) {
                                setPhoneValue('');
                                currentPhoneValue = '';
                            }
                            setCountry(newCountry);
                            onChangeHandler(newCountry, currentPhoneValue);
                            phoneInputRef.current?.focus();
                        }}
                        availableCountries={availableCountries}
                        priorityCountries={priorityCountries}
                        libPhoneNumberAPI={libPhoneNumberAPI}
                        invalid={invalid}
                        disabled={isComponentDisabled}
                        metadataVariant={metadataVariant}
                    />
                    <Input
                        ref={inputRefMultiplexer}
                        size="large"
                        onChange={(value) => {
                            setPhoneValue(value);
                            onChangeHandler(country, value);
                        }}
                        formatter={phoneFormatter}
                        value={phoneValue}
                        invalid={invalid}
                        disabled={isComponentDisabled}
                        inputMode="tel"
                        data-qa="magritte-phone-input-national-number-input"
                    />
                </div>
                <FormHelper
                    invalid={invalid}
                    description={description}
                    disabled={isComponentDisabled}
                    errorMessage={errorMessage}
                    data-qa="magritte-phone-input-form-helper"
                />
            </div>
        );
    }
);

PhoneInput.displayName = 'PhoneInput';
